<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import validations, { getSectorNameErrorMessage } from './validations';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const name = ref('');
const description = ref('');
const selectedTeams = ref([]);

const v$ = useVuelidate(validations, { name, description });

const uiFlags = computed(() => getters['sectors/getUIFlags'].value);
const teamsList = computed(() => getters['teams/getTeams'].value);

// Only show teams not yet assigned to any sector
const availableTeams = computed(() =>
  teamsList.value.filter(t => !t.sector_id)
);

const nameErrorMessage = computed(() =>
  t(getSectorNameErrorMessage(v$.value))
);

const onClose = () => emit('close');

const addSector = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  try {
    await store.dispatch('sectors/create', {
      name: name.value,
      description: description.value,
      team_ids: selectedTeams.value.map(t => t.id),
    });
    await store.dispatch('teams/get');
    useAlert(t('SECTOR_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(error?.message || t('SECTOR_MGMT.ADD.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('SECTOR_MGMT.ADD.TITLE')"
      :header-content="$t('SECTOR_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addSector">
      <woot-input
        v-model="name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :label="$t('SECTOR_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('SECTOR_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="nameErrorMessage"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <woot-input
        v-model="description"
        class="w-full"
        :label="$t('SECTOR_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('SECTOR_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('SECTOR_MGMT.FORM.TEAMS.LABEL') }}
        </label>
        <multiselect
          v-model="selectedTeams"
          :options="availableTeams"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="$t('SECTOR_MGMT.FORM.TEAMS.PLACEHOLDER')"
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        >
          <template #noOptions>{{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}</template>
        </multiselect>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('SECTOR_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <Button
          :label="$t('SECTOR_MGMT.FORM.CREATE')"
          :disabled="v$.name.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
          @click.prevent="addSector"
        />
      </div>
    </form>
  </div>
</template>
